import React, { useState, useMemo, useCallback, useRef } from 'react';
import shortid from 'shortid';
import { observer } from 'mobx-react';
import { computed, observable, isObservableArray, toJS, makeObservable } from 'mobx';
import { TextField, DefaultButton, ActionButton, ComboBox, DatePicker, mergeStyleSets } from '@fluentui/react';

import { omit, areStringsInsensitivelyEqual } from 'shared/utils/utils';
import { toSubstitution, isSubstitutionPattern } from 'shared/utils/querySubstitution';
import { parseFullName } from 'shared/utils/queryAnalysis';

import SearchableComboBox, { TextFieldWithCallout } from 'src/components/base/SearchableComboBox';
import TargetPicker from 'src/components/base/TargetPicker';
import TreeList, { useTreeListFiltering, getItem } from 'src/components/base/TreeList';
import { filterDateFromString, filterDateToString, isArrayFilterValue, JOIN_OPERATOR, OPERATORS } from 'shared/utils/filterManager';

const classNames = mergeStyleSets({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    height: '100%',
    backgroundColor: '#F4F4F4',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: 16,
    selectors: {
      '& > *': {
        flex: 1,
      },
      '& > :nth-child(1)': {
        marginRight: 16,
      },
    },
  },
  filterJoin: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  filterMain: {
    padding: '10px 16px 16px 16px',
    backgroundColor: 'white',
  },
  filterMainCaption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filterBody: {
    marginBottom: -4,
    selectors: {
      '& > *': {
        marginBottom: 16,
      },
    },
  },

  treeList: {
    margin: '8px 0px',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  withTargetPicker: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
  },
});


const TRUE_FALSE_VALUES = ['True', 'False'];

@observer
export default class FilterManager extends React.Component {
  @observable filters = [];

  @computed get isValidFilters() {
    return !this.filters.some(i => !i.name || !i.operator.length ||
      (!i.value.length && !['=', '!='].includes(i.operator))
    );
  }

  @computed get fields() {
    function getOptions(field) {
      if (field.options) {
        return field.options;
      }

      if (field.rawField.type === 'boolean') {
        return TRUE_FALSE_VALUES.map(x => ({ key: x, text: x }));
      }

      if (field.rawField.picklistValues) {
        return field.rawField.picklistValues.map(i => ({ key: i.value, text: i.label || i.value }))
      }

      return [];
    }

    function mapFields(fields) {
      return fields.map(field => ({
        ...field,
        options: getOptions(field),
        descendants: mapFields(field.descendants)
      }));
    }

    return mapFields(this.props.fields);
  }

  constructor(props) {
    super(props);
    makeObservable(this);
    this.initializeFilters();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.filters !== this.props.filters) {
      this.initializeFilters()
    }
  }

  initializeFilters() {
    this.filters = (this.props.filters || []).map(i => ({ ...i, id: shortid.generate() }));
    if (!this.filters.length) {
      this.handleAddFilterClick();
    }
  }

  render() {
    return (
      <div className={classNames.root}>
        { this.filters.map((filter, index) => (
          <Filter key={filter.id}
            index={index + 1}
            onChange={(data) => this.handleChangeFilter(index, data)}
            onDelete={() => this.handleDeleteFilter(index)}
            data={filter}
            fields={this.fields}
            handlers={this.props.handlers}
          />
        ))}

        <div className={classNames.buttons}>
          <DefaultButton primary={true} text='Add Filter'
            onClick={this.handleAddFilterClick} />

          <DefaultButton primary={true} text='Apply'
            disabled={!this.isValidFilters}
            onClick={this.handleApplyClick} />
        </div>
      </div>
    )
  }

  handleAddFilterClick = () => {
    this.filters.push({
      id: shortid.generate(),
      join: JOIN_OPERATOR.AND,
      operator: '',
      value: '',
      isSubstitution: false,
      name: '',
      fieldId: '',
    });
  }

  handleChangeFilter = (index, data) => {
    this.filters = this.filters.map((item, i) => index === i ? data : item);
  }

  handleDeleteFilter = (index) => {
    this.filters.splice(index, 1);
  }

  handleApplyClick = () => {
    this.props.onApply(this.filters.map(i => omit(i, 'id', 'fieldId')));
  }
}


// --- Value Selectors ---

const SubstitutionSelector = ({ onChange }) => {
  function handleTargetChanged(target) {
    onChange(toSubstitution({ address: target.addresses[0].address }), true);
  }

  return <TargetPicker selectionOnly={true} showRange={false} onChange={handleTargetChanged}/>
}

export const SimpleValue = ({ value, onChange, plural }) => {
  if (plural) {
    value = (Array.isArray(value) || isObservableArray(value)) ? value.join(', ') : [value];
  }

  function handleValueChange(event) {
    const value = event.target.value;
    onChange(
      plural
        ? value.split(',').map(i => i.trim())
        : value,
    isSubstitutionPattern(value)
    );
  }

  return (
    <div className={classNames.withTargetPicker}>
      <TextField styles={{ root: { width: '100%' } }} placeholder={'Enter the Value'} value={value} onChange={handleValueChange} />
      <SubstitutionSelector onChange={onChange}/>
    </div>
  );
}

export const SelectorValue = ({ value, options, onChange, plural, isSubstitution }) => {
  if (!isSubstitution && !plural && (Array.isArray(value) || isObservableArray(value))) {
    value = value.shift();
  }
  if (plural) {
    options = options.map(i => {
      const selected = !isSubstitution &&
        ((Array.isArray(value) || isObservableArray(value))
          ? value.includes(i.key)
          : i.key === value);
      return {...i, selected };
    });
  }

  function handleValueChange(_, item) {
    onChange(plural ? options.filter(i => item.key === i.key ? item.selected : i.selected).map(i => i.key) : item.key);
  }

  return (
    <div className={classNames.withTargetPicker}>
      <ComboBox
        placeholder="Select Value"
        useComboBoxAsMenuWidth={true}
        multiSelect={plural}
        selectedKey={isSubstitution ? [] : value}
        onChange={handleValueChange}
        options={options}
        text={isSubstitution ? value : undefined}
        styles={{ container: { width: '100%' } }}
      />
      <SubstitutionSelector onChange={onChange}/>
    </div>
  )
}

export const DateValue = ({ value, fieldType, isSubstitution, onChange }) => {
  function handleValueChange(date) {
    onChange(filterDateToString(fieldType, date));
  }

  function handleFormatDate(date) {
    return value;
  }

  return (
    <div className={classNames.withTargetPicker}>
      <DatePicker
        placeholder={'Select Date'}
        showMonthPickerAsOverlay={true}
        value={isSubstitution ? new Date(Date.now()) : (value && filterDateFromString(value))} // use fake to to make control happy
        formatDate={isSubstitution ? handleFormatDate : undefined}
        onSelectDate={handleValueChange}
        styles={{ root: { width: '100%'} }}
      />
      <SubstitutionSelector onChange={onChange}/>
    </div>
  )
}

// --- Types ---

const typesInfo = [
  {
    types: [ 'id', 'reference' ],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.IN, OPERATORS.NOTIN],
    component: SimpleValue,
  },
  {
    types: ['double', 'int', 'currency', 'percent', 'time'],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.GT, OPERATORS.GTEQ, OPERATORS.LS, OPERATORS.LSEQ, OPERATORS.IN, OPERATORS.NOTIN],
    component: SimpleValue,
  },
  {
    types: ['string', 'email', 'phone', 'textarea', 'url'],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.START, OPERATORS.END, OPERATORS.CONTAINS, OPERATORS.IN, OPERATORS.NOTIN],
    component: SimpleValue,
  },
  {
    types: ['date', 'datetime'],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.GT, OPERATORS.GTEQ, OPERATORS.LS, OPERATORS.LSEQ],
    component: DateValue,
  },
  {
    types: [ 'picklist', 'enumeration' ],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.IN, OPERATORS.NOTIN],
    component: SelectorValue,
  },
  {
    types: [ 'multipicklist' ],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ, OPERATORS.INCLUDE, OPERATORS.EXCLUDE],
    component: SelectorValue,
  },
  {
    types: [ 'boolean' ],
    operators: [OPERATORS.EQ, OPERATORS.NOTEQ],
    component: SelectorValue,
  },
]

const operatorsInfo = {
  [OPERATORS.EQ]: 'Equal to',
  [OPERATORS.NOTEQ]: 'Not Equal to',
  [OPERATORS.IN]: 'In',
  [OPERATORS.NOTIN]: 'Not In',
  [OPERATORS.GT]: 'Greater Than',
  [OPERATORS.GTEQ]: 'Greater Than or Equal to',
  [OPERATORS.LS]: 'Less Than',
  [OPERATORS.LSEQ]: 'Less Than or Equal to',
  [OPERATORS.START]: 'Starts with',
  [OPERATORS.END]: 'Ends with',
  [OPERATORS.CONTAINS]: 'Contains',
  [OPERATORS.INCLUDE]: 'Includes',
  [OPERATORS.EXCLUDE]: 'Excludes'
};


function Filter({ index, data, onDelete, onChange, handlers, fields }) {
  // const [ operator, setOperator ] = useState(data.operator);
  const { operator } = data;

  function handleJoinChange(join) {
    onChange({ ...data, join });
  }

  function handleSelectedFieldChanged(fieldId) {
    const { fullName: name } = getItem(fields, fieldId);
    onChange({ ...data, name, operator: '', value: '', fieldId });
  }

  function handleOperatorChange(operator) {
    onChange({ ...data, operator });
  }

  function handleValueChange(value, isSubstitution = false) {
    onChange({ ...data, value, isSubstitution });
  }

  function findField(fullName) {
    const names = parseFullName(fullName);
    // TODO: common function to get relationhsips and fields including polymorphic

    const tryFindRelationship = (parent, index) => {
      if (!parent) { return parent; }
      const name = names[index];
      const isRelationship = names.length - 1 === index;
      if (!isRelationship) {
        return parent.descendants.find(x => areStringsInsensitivelyEqual(name, x.name));
      }

      for (const child of parent.descendants) {
        const { relationshipName } = child.rawField;
        if (!relationshipName || !areStringsInsensitivelyEqual(name, relationshipName)) {
          continue;
        }

        if (!child.isPolymorphic) {
          return child;
        }

        for (const polymorphicChild of child.descendants) {
          if (tryFindRelationship(polymorphicChild, index + 1)) {
            return polymorphicChild;
          }
        }
      }

      return null;
    };

    return tryFindRelationship({ descendants: fields }, 0);
  }

  let ValueField = SimpleValue, fieldType = 'string';
  const operators = [], options = [];

  const selectedField = useMemo(() => data.name
    ? data.fieldId ? getItem(fields, data.fieldId) : findField(data.name)
    : null, [fields, data.name]);

  const { rawField } = selectedField || {}; //TODO: select control
  if (rawField) {
    const type = typesInfo.find(i => i.types.includes(rawField.type)) || typesInfo[0];
    operators.push(...type.operators.map(i => ({ key: i, text: operatorsInfo[i] })));
    options.push(...selectedField.options);

    fieldType = rawField.type;
    ValueField = type.component;
  }

  const plural = isArrayFilterValue(fieldType, operator);

  return (
    <div>
      { index !== 1 &&
        <div className={classNames.filterJoin}>
          <span>Join With</span>
          <ComboBox
            options={Object.entries(JOIN_OPERATOR).map(([text, key]) => ({key ,text}))}
            selectedKey={data.join}
            onChange={(event, item) => handleJoinChange(item.key)}
            useComboBoxAsMenuWidth={true}
          />
        </div>
      }

      <div className={classNames.filterMain}>
        <div className={classNames.filterMainCaption}>
          <span className="ms-font-l">{`Filter #${index}`}</span>
          <ActionButton iconProps={{ iconName: 'RecycleBin' }} onClick={onDelete}>Delete</ActionButton>
        </div>

        <div className={classNames.filterBody}>
          <FieldSelector
            fields={fields}
            selectedField={selectedField}
            onSelectedFieldChanged={handleSelectedFieldChanged}
            handlers={handlers}
            onLoadFields={handlers.onLoadFields}
            onFilterApplied={handlers.onFieldsFilterApplied}
            onRenderTitle={handlers.onRenderFieldTitle}
          />

          <SearchableComboBox
            placeholder="Setup the Query Filters"
            selectedKey={operator}
            onChange={(event, item) => handleOperatorChange(item.key)}
            options={operators}
          />

          <ValueField value={data.value} onChange={handleValueChange} plural={plural}
            isSubstitution={data.isSubstitution} options={options} fieldType={fieldType} />
        </div>
      </div>
    </div>
  )
}


export function FieldSelector({ fields, selectedField, onSelectedFieldChanged, handlers, ...restProps }) {
  const { onLoadFields, onFieldsFilterApplied, onRenderFieldTitle } = handlers;

  const [filter, setFilter] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [isDropdownOpened, setDropdownOpened] = useState(false);

  const selectedIds = useMemo(() => [selectedField].filter(Boolean).map(x => x.id), [selectedField]);
  const selectedItemText = useMemo(() => selectedField?.fullDisplayName ?? '', [selectedField]);


  // --- Handlers ---
  function handleItemsSelected(areSelected, items, allSelectedItems) {
    onSelectedFieldChanged(allSelectedItems.length ? allSelectedItems[0] : null);
    setFilter(null);
    setDropdownOpened(false);
  }

  async function handleItemsCollapsed(areCollapsed, items, itemsToLoad, allExpandedItems) {
    setExpandedIds([...allExpandedItems, ...(await onLoadFields(itemsToLoad))]);
  }

  function handleFilterChanged(filter) {
    // TODO: prefilter
    setFilter(filter);
  }

  function handleOpenDropdown(open) {
    if (isDropdownOpened !== open) {
      setDropdownOpened(open);
      if (!!filter) { setFilter(null); }
    }
  }

  const {
    items: filteredItems,
    onItemSelected,
    onItemCollapsed,
    onFilterChanged,
  } = useTreeListFiltering({
    items: fields, selectedIds, expandedIds,
    filter, limitSelectionByNumber: 1, onItemsCollapsed: handleItemsCollapsed,
    onItemsSelected: handleItemsSelected, onFilterChanged: handleFilterChanged,
    onFilterApplied: onFieldsFilterApplied,
  });

  const handleItemClicked = (item) => {
    const { isActive, isSelectable } = item;
    isActive && isSelectable && onItemSelected(true, item);
  }

  const treeComponentPropsRef = useRef();
  treeComponentPropsRef.current = { items: filteredItems, onItemCollapsed, onItemClicked: handleItemClicked };

  const treeComponent = useCallback(() => {
    return <TreeList
      key="TreeList"
      className={classNames.treeList}
      onRenderItemTitle={onRenderFieldTitle}
      onShouldVirtualize={() => false}
      {...treeComponentPropsRef.current}
    />
  }, []);

  return (
    <div>
      <TextFieldWithCallout
        placeholder="Select Field to Filter by"
        query={filter}
        text={selectedItemText}
        isDropdownOpened={isDropdownOpened}
        onQueryChanged={onFilterChanged}
        onOpenDropdown={handleOpenDropdown}
        component={treeComponent}
      />
    </div>
  );
};

const OperatorToKeyword = {
  [OPERATORS.INCLUDE]: 'INCLUDES',
  [OPERATORS.EXCLUDE]: 'EXCLUDES',
  [OPERATORS.IN]: 'IN',
  [OPERATORS.NOTIN]: 'NOT IN',
}

// --- Funtions ---

export function createFilterNode(filter, fields) {
  function toLiteralType(type) {
    if (['double', 'int', 'currency', 'percent'].includes(type)) { return 'DECIMAL' }
    if (['boolean', 'datetime', 'date'].includes(type)) { return type.toUpperCase() }
    return 'STRING';
  }

  const field = getItem(fields, filter.fieldId);
  const result = { field: field.fullName, literalType: toLiteralType(field.rawField.type) };

  if ([OPERATORS.IN, OPERATORS.NOTIN, OPERATORS.EXCLUDE, OPERATORS.INCLUDE].includes(filter.operator)) {
    const value = toJS(filter.value);
    const operator = OperatorToKeyword[filter.operator];
    const replace = filter.isSubstitution
      ? { from: `${operator} ('${value}')`, to: `${operator} ${value}` }
      : undefined;
    return { ...result, operator, replace,
      value: Array.isArray(value) ? value : [value]
    }
  }

  if ([OPERATORS.START, OPERATORS.END, OPERATORS.CONTAINS].includes(filter.operator)) {
    const value = filter.value;
    return { ...result, operator: 'LIKE',
      value: (filter.operator === OPERATORS.START) ? `${value}%`
        : (filter.operator === OPERATORS.END ? `%${value}` : `%${value}%`),
    }
  }

  const nullValue = 'NULL';
  const isNull = !filter.value.toString().length || filter.value.toUpperCase() === nullValue;
  const value = isNull ? { literalType: nullValue, value: nullValue } : { value: filter.value };
  return { ...result, operator: filter.operator, ...value };
}


// TODO: use when filters parsing is ready
export function getFilterStates(filters) {
  return filters.map(filter => {
    const { field, value: initialValue, isSubstitution } = filter;
    const { rawField, id } = field;

    // TODO: take '%' at the start and at the end into account
    const operator = filter.operator === 'like' ? OPERATORS.CONTAINS : filter.operator;
    const value = isSubstitution
      ? initialValue
      : rawField.type === 'boolean'
        ? TRUE_FALSE_VALUES.find(x => areStringsInsensitivelyEqual(initialValue, x))
        : !!rawField.picklistValues
          ? initialValue
              .map(value => rawField.picklistValues.find(y => areStringsInsensitivelyEqual(y.label, value))?.value)
              .filter(Boolean)
          : initialValue;

    return { ...filter, value, field: id, operator };
  });
}
